import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["filterButton", "filterLabel"];

  connect() {
    this.updateFilterState();
  }

  toggleFilter(event) {
    const input = event.currentTarget;

    if (input.dataset.filterMultiSelect === "true") {
      this.syncMultiSelectGroupHidden(input);
    } else {
      this.syncHiddenFromInput(input);
    }
    this.updateFilterState();
  }

  syncMultiSelectHidden(event) {
    this.syncMultiSelectElement(event.currentTarget)
    this.updateFilterState()
  }

  applyFilters() {
    this._syncFilterFields();
    this.updateFilterState();
    this.submitForm();
  }

  resetFilters() {
    const inputs = this.filterInputs();
    const groups = this.groupsFromInputs(inputs);

    this._resetFilterFields();

    inputs.forEach((input) => {
      if (input.tagName === "SELECT") {
        input.value = "";
      } else {
        input.checked = false;
      }
    });

    groups.forEach(({ target, defaultInput, multiSelect, isSelect }) => {
      if (multiSelect) {
        this.element
          .querySelectorAll(`input[type="hidden"][name="${target}"]`)
          .forEach((h) => h.remove());
        return;
      }

      if (isSelect) {
        const selectEl = this.element.querySelector(`select[data-filter-group][data-filter-target="${target}"]`);
        if (selectEl) this.syncHiddenFromInput(selectEl);
        return;
      }

      if (defaultInput) {
        defaultInput.checked = true;
        this.syncHiddenFromInput(defaultInput);
        return;
      }

      const hidden = this.element.querySelector(`input[name="${target}"]`);
      if (hidden) hidden.value = "";
    });

    this.filterMultiSelectElements().forEach((element) => {
      this.multiSelectControllerFor(element)?.clearSelection({ emitChange: false })
      this.clearMultiSelectHidden(element.dataset.filterMultiSelectName)
    })
    this.syncDependentMultiSelects()

    this.updateFilterState();
    this.submitForm();
  }

  submitForm() {
    const form = this.element.closest("form");
    form?.requestSubmit();
  }

  clearAndSubmit(event) {
    event.preventDefault();

    const currentTarget = event.currentTarget;
    const form = document.getElementById(currentTarget.dataset.filterFormId);
    if (!form) return;

    const dropdown = window.$(currentTarget).closest("[data-tippy-root]");

    if (dropdown.length) {
      dropdown.resetMdFields();
    }

    form.requestSubmit();
  }

  filterInputs() {
    return Array.from(
      this.element.querySelectorAll(
        'input[data-filter-group][data-filter-type="filter"], select[data-filter-group][data-filter-type="filter"]'
      )
    );
  }

  groupsFromInputs(inputs) {
    const groups = new Map();
    inputs.forEach((input) => {
      const group = input.dataset.filterGroup;

      if (!groups.has(group)) {
        groups.set(group, {
          target: input.dataset.filterTarget,
          defaultInput: null,
          multiSelect: input.dataset.filterMultiSelect === "true",
          isSelect: input.tagName === "SELECT",
        });
      }

      if (input.dataset.filterDefault === "true") {
        groups.get(group).defaultInput = input;
      }
    });
    return groups;
  }

  syncHiddenFromInput(input) {
    const targetName = input.dataset.filterTarget;

    const hidden = this.element.querySelector(`input[name="${targetName}"]`);
    if (hidden) {
      hidden.value = input.tagName === "SELECT" ? input.value : (input.dataset.filterValue || "");
    }
  }

  syncMultiSelectGroupHidden(input) {
    const targetName = input.dataset.filterTarget;
    const groupName = input.dataset.filterGroup;

    const checked = Array.from(
      this.element.querySelectorAll(
        `input[type="checkbox"][data-filter-group="${groupName}"]:checked`
      )
    ).filter((cb) => !cb.closest(".display-none"));

    this.rebuildHidden(targetName, checked.map((cb) => cb.dataset.filterValue));
  }

  groupState() {
    const inputs = this.element.querySelectorAll(
      'input[data-filter-group][data-filter-type="filter"], select[data-filter-group][data-filter-type="filter"]'
    );
    const groups = new Map();

    inputs.forEach((input) => {
      const group = input.dataset.filterGroup;

      if (!groups.has(group)) {
        groups.set(group, {
          target: input.dataset.filterTarget,
          defaultValue: null,
          multiSelect: input.dataset.filterMultiSelect === "true",
        });
      }

      if (input.dataset.filterDefault === "true") {
        groups.get(group).defaultValue = input.dataset.filterValue || "";
      }
    });

    let count = 0;
    groups.forEach(({ target, defaultValue, multiSelect }) => {
      if (multiSelect) {
        const hiddens = this.element.querySelectorAll(`input[type="hidden"][name="${target}"]`);
        count += hiddens.length;
      } else {
        const hidden = this.element.querySelector(`input[name="${target}"]`);
        const current = hidden?.value || "";
        const applied = defaultValue === null ? Boolean(current) : current !== defaultValue;
        if (applied) count += 1;
      }
    });

    // data-filter-field convention fields
    this.element.querySelectorAll('input[type="hidden"][data-filter-field]').forEach((h) => { if (h.value) count++; });
    this._eachMultiGroup((name) => { count += this.element.querySelectorAll(`input[type="hidden"][name="${name}"]`).length; });
    this.filterMultiSelectTargetNames().forEach((name) => {
      count += this.element.querySelectorAll(`input[type="hidden"][name="${name}"]`).length
    })
    return { count, applied: count > 0 };
  }

  _syncFilterFields() {
    this.filterMultiSelectElements().forEach((element) => this.syncMultiSelectElement(element))
    this.element.querySelectorAll('input[type="hidden"][data-filter-field]').forEach((h) => {
      const v = this._visibleFilterInput(h.dataset.filterField);
      if (v) h.value = v.value;
    });
    this._eachMultiGroup((name, values) => {
      this.rebuildHidden(name, values);
    });
  }

  _resetFilterFields() {
    this.element.querySelectorAll("[data-filter-field]").forEach((i) => {
      if (i.type === "hidden") i.value = "";
      else if (i.dataset.filterMulti !== undefined) i.checked = false;
      else if (i.type === "radio") i.checked = i.value === "";
      else if (i.tagName === "SELECT") { i.value = ""; i.classList.remove("filled"); }
      else i.value = "";
    });
    this._eachMultiGroup((name) => {
      this.rebuildHidden(name, []);
    });
  }

  _eachMultiGroup(callback) {
    const groups = new Map();
    this.element.querySelectorAll("[data-filter-field][data-filter-multi]").forEach((i) => {
      const name = i.dataset.filterFieldName;
      if (!groups.has(name)) groups.set(name, []);
      if (i.closest(".tippy-content") && i.checked) groups.get(name).push(i.value);
    });
    groups.forEach((values, name) => callback(name, values));
  }

  _visibleFilterInput(field) {
    const all = Array.from(this.element.querySelectorAll(`[data-filter-field="${field}"]:not([type=hidden])`));
    const radios = all.filter((i) => i.type === "radio");
    if (radios.length) return radios.find((i) => i.closest(".tippy-content") && i.checked) || radios.find((i) => i.checked);
    return all.find((i) => i.closest(".tippy-content")) || all[0];
  }

  filterMultiSelectElements() {
    return Array.from(this.element.querySelectorAll("[data-filter-multi-select-name]"))
  }

  filterMultiSelectTargetNames() {
    return [...new Set(this.filterMultiSelectElements().map((element) => element.dataset.filterMultiSelectName).filter(Boolean))]
  }

  multiSelectControllerFor(element) {
    return this.application.getControllerForElementAndIdentifier(element, "multi-select-dropdown")
  }

  syncMultiSelectElement(element) {
    const targetName = element.dataset.filterMultiSelectName

    const controller = this.multiSelectControllerFor(element)
    const selectedValues = controller ? controller.selectedOptions.map((option) => option.value) : []

    this.rebuildHidden(targetName, selectedValues)
  }

  clearMultiSelectHidden(targetName) {
    this.rebuildHidden(targetName, [])
  }

  rebuildHidden(name, values) {
    this.element
      .querySelectorAll(`input[type="hidden"][name="${name}"]`)
      .forEach((hidden) => hidden.remove())

    values.forEach((value) => {
      this.element.prepend(Object.assign(document.createElement("input"), { type: "hidden", name, value }))
    })
  }

  syncDependentMultiSelects() {
    this.element.querySelectorAll('[data-controller~="dependent-multi-select"]').forEach((element) => {
      this.application.getControllerForElementAndIdentifier(element, "dependent-multi-select")?.sync()
    })
  }

  updateFilterState() {
    if (!this.hasFilterButtonTarget) return;

    const { count } = this.groupState();
    const baseLabel = this.filterButtonTarget.dataset.filterLabel || "";

    this.filterButtonTarget.classList.toggle("bg-blue-gray-100", count > 0);

    if (this.hasFilterLabelTarget) {
      this.filterLabelTarget.textContent = count > 0 ? `${baseLabel} (${count})` : baseLabel;
    }
  }
}
